import React, { useEffect, useState, useCallback } from 'react';
import { fmtDateTime } from '../utils/dateUtils';

const today = () => new Date().toISOString().split('T')[0];

function AdminAuditTrails({ token }) {
  const [activeTab, setActiveTab] = useState('login-history');
  const [loginRows, setLoginRows] = useState([]);
  const [auditRows, setAuditRows] = useState([]);
  const [loginFilter, setLoginFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Date filters
  const [loginFrom, setLoginFrom] = useState(today());
  const [loginTo, setLoginTo] = useState(today());
  const [auditFrom, setAuditFrom] = useState(today());
  const [auditTo, setAuditTo] = useState(today());

  // Property filter (audit trails only)
  const [properties, setProperties] = useState([]);
  const [auditPropertyFilter, setAuditPropertyFilter] = useState('');

  const hdrs = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Load property list for filter dropdown
  useEffect(() => {
    if (!token) return;
    fetch('/api/properties/accessible', { headers: hdrs() })
      .then(r => r.ok ? r.json() : [])
      .then(data => setProperties(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [token, hdrs]);

  const loadLoginHistory = useCallback(async () => {
    const params = new URLSearchParams({ limit: '500', from: loginFrom, to: loginTo });
    const res = await fetch(`/api/auth/admin/login-history?${params}`, { headers: hdrs() });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to load login history');
    setLoginRows(Array.isArray(data) ? data : []);
  }, [loginFrom, loginTo, hdrs]);

  const loadAuditTrails = useCallback(async () => {
    const params = new URLSearchParams({ limit: '1000', from: auditFrom, to: auditTo });
    if (auditPropertyFilter) params.set('property_id', auditPropertyFilter);
    const res = await fetch(`/api/auth/admin/audit-trails?${params}`, { headers: hdrs() });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to load audit trails');
    setAuditRows(Array.isArray(data) ? data : []);
  }, [auditFrom, auditTo, auditPropertyFilter, hdrs]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      await Promise.all([loadLoginHistory(), loadAuditTrails()]);
    } catch (e) {
      setError(e.message || 'Failed to load audit data');
    } finally {
      setLoading(false);
    }
  }, [loadLoginHistory, loadAuditTrails]);

  // Load on mount and when filters change
  useEffect(() => { loadAll(); }, [loadAll]);

  const filterStyle = { display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem', padding: '0.8rem 1rem', background: '#f8fafc', borderRadius: 8, border: '1px solid #e5e7eb' };
  const labelStyle = { fontSize: '0.8rem', fontWeight: 600, color: '#475569' };
  const inputStyle = { padding: '0.4rem 0.6rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.82rem', outline: 'none' };

  return (
    <div className="page-shell">
      <h2 style={{ color: 'var(--theme-primary-dark)', marginBottom: '0.8rem' }}>Admin Audit Trails</h2>
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
        <button
          onClick={() => setActiveTab('login-history')}
          style={{ padding: '0.55rem 0.9rem', border: 'none', borderRadius: '6px', background: activeTab === 'login-history' ? 'var(--theme-primary-dark)' : '#dcdde1', color: activeTab === 'login-history' ? '#fff' : '#2f3640', cursor: 'pointer', fontWeight: 600 }}
        >
          Login History
        </button>
        <button
          onClick={() => setActiveTab('audit-trails')}
          style={{ padding: '0.55rem 0.9rem', border: 'none', borderRadius: '6px', background: activeTab === 'audit-trails' ? 'var(--theme-primary-dark)' : '#dcdde1', color: activeTab === 'audit-trails' ? '#fff' : '#2f3640', cursor: 'pointer', fontWeight: 600 }}
        >
          Audit Trails
        </button>
      </div>
      {error && <div className="error-banner">{error}</div>}

      {/* ═══ LOGIN HISTORY TAB ═══ */}
      {activeTab === 'login-history' && (() => {
        const failedCount = loginRows.filter(r => !r.success).length;
        const successCount = loginRows.filter(r => r.success).length;
        const filteredRows = loginFilter === 'all' ? loginRows : loginFilter === 'failed' ? loginRows.filter(r => !r.success) : loginRows.filter(r => r.success);
        return (
        <>
          {/* Date filter */}
          <div style={filterStyle}>
            <label style={labelStyle}>From</label>
            <input type="date" value={loginFrom} onChange={e => setLoginFrom(e.target.value)} style={inputStyle} />
            <label style={labelStyle}>To</label>
            <input type="date" value={loginTo} onChange={e => setLoginTo(e.target.value)} style={inputStyle} />
            <button onClick={loadAll} className="btn-success" style={{ padding: '0.4rem 1rem', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem' }}>
              Fetch
            </button>
            {loading && <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>Loading...</span>}
          </div>

          {/* Summary cards */}
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            <div style={{ padding: '0.6rem 1.2rem', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, minWidth: 120 }}>
              <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#166534' }}>{successCount}</div>
              <div style={{ fontSize: '0.75rem', color: '#166534' }}>Successful</div>
            </div>
            <div style={{ padding: '0.6rem 1.2rem', background: failedCount > 0 ? '#fef2f2' : '#f9fafb', border: `1px solid ${failedCount > 0 ? '#fecaca' : '#e5e7eb'}`, borderRadius: 8, minWidth: 120 }}>
              <div style={{ fontSize: '1.3rem', fontWeight: 700, color: failedCount > 0 ? '#991b1b' : '#6b7280' }}>{failedCount}</div>
              <div style={{ fontSize: '0.75rem', color: failedCount > 0 ? '#991b1b' : '#6b7280' }}>Failed Attempts</div>
            </div>
            <div style={{ padding: '0.6rem 1.2rem', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, minWidth: 120 }}>
              <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#1e40af' }}>{loginRows.length}</div>
              <div style={{ fontSize: '0.75rem', color: '#1e40af' }}>Total</div>
            </div>
          </div>

          {/* Filter tabs */}
          <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.75rem' }}>
            {[
              { key: 'all', label: `All (${loginRows.length})` },
              { key: 'failed', label: `Failed (${failedCount})` },
              { key: 'success', label: `Success (${successCount})` },
            ].map(f => (
              <button key={f.key} onClick={() => setLoginFilter(f.key)}
                style={{
                  padding: '0.35rem 0.8rem', borderRadius: 6, border: '1px solid',
                  borderColor: loginFilter === f.key ? (f.key === 'failed' ? '#ef4444' : 'var(--theme-primary-dark)') : '#d1d5db',
                  background: loginFilter === f.key ? (f.key === 'failed' ? '#ef4444' : 'var(--theme-primary-dark)') : '#fff',
                  color: loginFilter === f.key ? '#fff' : '#374151',
                  cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
                }}
              >{f.label}</button>
            ))}
          </div>

          <div className="card" style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f5f6fa' }}>
                  <th style={{ padding: '0.6rem', textAlign: 'left' }}>Time</th>
                  <th style={{ padding: '0.6rem', textAlign: 'left' }}>User</th>
                  <th style={{ padding: '0.6rem', textAlign: 'left' }}>Role</th>
                  <th style={{ padding: '0.6rem', textAlign: 'left' }}>Status</th>
                  <th style={{ padding: '0.6rem', textAlign: 'left' }}>Failure Reason</th>
                  <th style={{ padding: '0.6rem', textAlign: 'left' }}>Login ID</th>
                  <th style={{ padding: '0.6rem', textAlign: 'left' }}>IP Address</th>
                  <th style={{ padding: '0.6rem', textAlign: 'left' }}>User Agent</th>
                  <th style={{ padding: '0.6rem', textAlign: 'left' }}>Logout At</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => (
                  <tr key={row.id} style={{ borderTop: '1px solid #eef2f7', background: !row.success ? '#fef2f2' : undefined }}>
                    <td style={{ padding: '0.6rem', fontSize: '0.82rem' }}>{row.login_at ? fmtDateTime(row.login_at) : '-'}</td>
                    <td style={{ padding: '0.6rem', fontWeight: 600 }}>{row.username || '-'}</td>
                    <td style={{ padding: '0.6rem' }}>{row.role || '-'}</td>
                    <td style={{ padding: '0.6rem' }}>
                      <span style={{
                        padding: '0.15rem 0.5rem', borderRadius: 12, fontSize: '0.75rem', fontWeight: 600,
                        background: row.success ? '#dcfce7' : '#fecaca',
                        color: row.success ? '#166534' : '#991b1b',
                      }}>
                        {row.success ? 'SUCCESS' : 'FAILED'}
                      </span>
                    </td>
                    <td style={{ padding: '0.6rem', color: '#991b1b', fontSize: '0.82rem' }}>{row.failure_reason || '-'}</td>
                    <td style={{ padding: '0.6rem', fontSize: '0.82rem' }}>{row.login_identifier || '-'}</td>
                    <td style={{ padding: '0.6rem', fontSize: '0.82rem', fontFamily: 'monospace' }}>{row.ip_address || '-'}</td>
                    <td style={{ padding: '0.6rem', fontSize: '0.72rem', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.user_agent || ''}>{row.user_agent || '-'}</td>
                    <td style={{ padding: '0.6rem', fontSize: '0.82rem' }}>{row.logout_at ? fmtDateTime(row.logout_at) : '-'}</td>
                  </tr>
                ))}
                {!filteredRows.length && (
                  <tr>
                    <td colSpan={9} style={{ padding: '0.8rem', color: '#7f8c8d' }}>No {loginFilter === 'failed' ? 'failed login attempts' : 'login history'} found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
        );
      })()}

      {/* ═══ AUDIT TRAILS TAB ═══ */}
      {activeTab === 'audit-trails' && (
        <>
          {/* Date + Property filter */}
          <div style={filterStyle}>
            <label style={labelStyle}>From</label>
            <input type="date" value={auditFrom} onChange={e => setAuditFrom(e.target.value)} style={inputStyle} />
            <label style={labelStyle}>To</label>
            <input type="date" value={auditTo} onChange={e => setAuditTo(e.target.value)} style={inputStyle} />
            <label style={labelStyle}>Property</label>
            <select value={auditPropertyFilter} onChange={e => setAuditPropertyFilter(e.target.value)}
              style={{ ...inputStyle, minWidth: 160 }}>
              <option value="">All Properties</option>
              {properties.map(p => (
                <option key={p.id} value={p.id}>{p.code ? `${p.code} - ${p.name}` : p.name}</option>
              ))}
            </select>
            <button onClick={loadAll} className="btn-success" style={{ padding: '0.4rem 1rem', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem' }}>
              Fetch
            </button>
            {loading && <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>Loading...</span>}
            <span style={{ fontSize: '0.75rem', color: '#94a3b8', marginLeft: 'auto' }}>{auditRows.length} records</span>
          </div>

          <div className="card" style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f5f6fa' }}>
                  <th style={{ padding: '0.6rem', textAlign: 'left' }}>Time</th>
                  <th style={{ padding: '0.6rem', textAlign: 'left' }}>User</th>
                  <th style={{ padding: '0.6rem', textAlign: 'left' }}>Action</th>
                  <th style={{ padding: '0.6rem', textAlign: 'left' }}>Method</th>
                  <th style={{ padding: '0.6rem', textAlign: 'left' }}>Path</th>
                  <th style={{ padding: '0.6rem', textAlign: 'left' }}>Status</th>
                  <th style={{ padding: '0.6rem', textAlign: 'left' }}>Property</th>
                  <th style={{ padding: '0.6rem', textAlign: 'left' }}>Device OS</th>
                  <th style={{ padding: '0.6rem', textAlign: 'left' }}>Device Type</th>
                  <th style={{ padding: '0.6rem', textAlign: 'left' }}>Browser</th>
                  <th style={{ padding: '0.6rem', textAlign: 'left' }}>Latency (ms)</th>
                </tr>
              </thead>
              <tbody>
                {auditRows.map((row) => (
                  <tr key={row.id} style={{ borderTop: '1px solid #eef2f7' }}>
                    <td style={{ padding: '0.6rem' }}>{row.created_at ? fmtDateTime(row.created_at) : '-'}</td>
                    <td style={{ padding: '0.6rem' }}>{row.username || '-'}</td>
                    <td style={{ padding: '0.6rem' }}>{row.action_name || '-'}</td>
                    <td style={{ padding: '0.6rem' }}>
                      <span style={{
                        padding: '0.1rem 0.4rem', borderRadius: 4, fontSize: '0.72rem', fontWeight: 600,
                        background: row.request_method === 'GET' ? '#dbeafe' : row.request_method === 'POST' ? '#dcfce7' : row.request_method === 'PUT' ? '#fef3c7' : row.request_method === 'DELETE' ? '#fecaca' : '#f3f4f6',
                        color: row.request_method === 'GET' ? '#1e40af' : row.request_method === 'POST' ? '#166534' : row.request_method === 'PUT' ? '#92400e' : row.request_method === 'DELETE' ? '#991b1b' : '#374151',
                      }}>
                        {row.request_method || '-'}
                      </span>
                    </td>
                    <td style={{ padding: '0.6rem', fontSize: '0.8rem', fontFamily: 'monospace' }}>{row.request_path || '-'}</td>
                    <td style={{ padding: '0.6rem' }}>
                      <span style={{
                        fontWeight: 600, fontSize: '0.8rem',
                        color: (row.status_code >= 200 && row.status_code < 300) ? '#166534' : row.status_code >= 400 ? '#991b1b' : '#6b7280',
                      }}>
                        {row.status_code ?? '-'}
                      </span>
                    </td>
                    <td style={{ padding: '0.6rem' }}>{row.property_name || '-'}</td>
                    <td style={{ padding: '0.6rem' }}>{row.device_os || '-'}</td>
                    <td style={{ padding: '0.6rem' }}>{row.device_type || '-'}</td>
                    <td style={{ padding: '0.6rem' }}>{row.browser_name ? `${row.browser_name}${row.browser_version ? ` ${row.browser_version}` : ''}` : '-'}</td>
                    <td style={{ padding: '0.6rem' }}>{row.response_time_ms ?? '-'}</td>
                  </tr>
                ))}
                {!auditRows.length && (
                  <tr>
                    <td colSpan={11} style={{ padding: '0.8rem', color: '#7f8c8d' }}>No audit trails found for selected date range</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default AdminAuditTrails;
